import { FormsInputDropdown, InputDropdownItem, FormsCheckBox } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useState, useContext } from 'react';
import { AdminProfessionals } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import { AdminTableContext } from '../../AdminTable';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';

type UpdateCohortFormData = {
  partnerId: number | null;
  cohortId: number | null;
  isDrop: boolean | null;
};

export const UpdateCohortModal = (props: {
  onClose: () => void;
  userId: number;
  currentCohort: string;
  currentPartner: string;
  proName: string;
}) => {
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { control, handleSubmit, watch, formState, setValue } = useForm<UpdateCohortFormData>({
    defaultValues: {
      partnerId: null,
      cohortId: null,
      isDrop: false,
    },
  });
  const selectedPartnerId = watch('partnerId');

  // Manage dropdown items, each partner has their own cohorts
  const { data: cohortsByPartner } = AdminProfessionals.useGetCohortsByPartner();
  const selectedPartner = cohortsByPartner?.partners.find(
    (partner) => partner.id == selectedPartnerId,
  );
  const cohortDropdownItems: InputDropdownItem[] =
    selectedPartner ? mapToDropdownItems(selectedPartner.cohorts) : [];

  cohortDropdownItems.unshift({
    textValue: -1,
    text: 'None',
  });
  if (selectedPartner?.name === 'TRANSFORM') {
    setValue('cohortId', null);
  }

  const queryClient = useQueryClient();
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const updateCohortMutator = AdminProfessionals.useUpdateCohort({
    mutation: {
      onMutate: () => {
        setModalStage('submitting');
      },
      onSuccess: () => {
        setModalStage('success');
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminProfessionals.getGetAdminProfessionalsQueryKey({
              ...adminQueryOptionsV2,
            }),
          ],
        });
      },
    },
  });

  const onSubmit: SubmitHandler<UpdateCohortFormData> = (data) => {
    if (data.partnerId) {
      updateCohortMutator.mutate({
        data: {
          userId: props.userId,
          partnerId: data.partnerId,
          cohortId: data.cohortId,
          isDrop: data.isDrop,
        },
      });
    }
  };

  return (
    <AdminFormModal
      modalStage={modalStage}
      title={`Cohort Update: ${props.proName}`}
      onClose={props.onClose}
      submitCTA={{
        buttonVariant: 'primary',
        text: 'Update Cohort',
        callback: () => handleSubmit(onSubmit)(),
      }}
    >
      <div className='flex flex-col'>
        <p className='mb-2'>
          <span className='font-semibold'>Current Partner:</span> {props.currentPartner}
        </p>
        <p className='mb-4'>
          <span className='font-semibold'>Current Cohort:</span> {props.currentCohort}
        </p>
        <FormsInputDropdown
          type='default'
          inputDisabled
          control={control}
          name='partnerId'
          label='New Partner'
          items={mapToDropdownItems(cohortsByPartner?.partners || [])}
          placeholder='Select a partner'
          rules={{ required: 'Partner selection is required' }}
          error={formState.errors.partnerId}
        />
        {selectedPartner?.name !== 'TRANSFORM' && (
          <FormsInputDropdown
            type='default'
            inputDisabled
            control={control}
            name='cohortId'
            label='New Cohort'
            items={cohortDropdownItems}
            placeholder='Select a cohort'
            rules={{ required: 'Cohort selection is required' }}
            error={formState.errors.cohortId}
          />
        )}
        {props.currentCohort && (
          <>
            <div>
              <FormsCheckBox control={control} name='isDrop' description='Record drop' size='md' />
            </div>
          </>
        )}
      </div>
    </AdminFormModal>
  );
};

const mapToDropdownItems = (items: { id: number; name: string }[]): InputDropdownItem[] => {
  return items.map((item) => {
    return {
      textValue: item.id,
      text: item.name,
    };
  });
};
